import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../SignUpClient.css';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) {
    return envBase.trim().replace(/\/+$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.hostname) {
    const currentHost = window.location.hostname;
    if (
      currentHost === 'localhost'
      || currentHost === '127.0.0.1'
      || currentHost === '::1'
      || currentHost === '[::1]'
    ) {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${currentHost}:8000/api`;
  }

  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;

const SignUpClient = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    dob: '',
    zip_code: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const navigate = useNavigate();

  // Password validation function
  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!minLength) return 'Password must be at least 8 characters long.';
    if (!hasUppercase) return 'Password must contain at least one uppercase letter.';
    if (!hasNumber) return 'Password must contain at least one number.';
    if (!hasSpecialChar) return 'Password must contain at least one special character.';
    return '';  // No errors
  };

  const handleSignUp = (e) => {
    e.preventDefault();

    // Validate password before sending request
    const passwordValidationMessage = validatePassword(formData.password);
    if (passwordValidationMessage) {
      setPasswordError(passwordValidationMessage);
      return;
    }

    // Ensure both passwords match
    if (formData.password !== formData.confirmPassword) {
      setMessage('the 2 passwords = ' + formData.password + ":" + formData.confirmPassword);
      setConfirmPasswordError("Passwords do not match.");
      return;
    }

    // Prepare user data for the request
    const signUpData = {
      username: formData.username,
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      role: 'client',
      phone_number: formData.phone_number,
      dob: formData.dob,
      zip_code: formData.zip_code,
      password: formData.password,
      confirm_password: formData.confirmPassword
    };

    axios.post(apiUrl('register/'),
      signUpData,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    )
    .then(response => {
      setMessage('Client account created successfully!');
      navigate('/login');  // Redirect to login after successful registration
    })
    .catch(error => {
      if (error.response?.data?.error) {
        setMessage(error.response.data.error);
        return;
      }
      if (error.request) {
        setMessage(`Unable to reach backend at ${apiUrl('register/')}.`);
        return;
      }
      setMessage('An error occurred during sign-up. Please try again.');
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        <h2>Create a Client Account</h2>
        <form onSubmit={handleSignUp} className="signup-form">
          {/* Username */}
          <div className="form-group">
            <label htmlFor="username">Username:</label>
            <input type="text" id="username" className="form-control"
              value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required />
          </div>

          {/* First Name */}
          <div className="form-group">
            <label htmlFor="first_name">First Name:</label>
            <input type="text" id="first_name" className="form-control"
              value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required />
          </div>

          {/* Last Name */}
          <div className="form-group">
            <label htmlFor="last_name">Last Name:</label>
            <input type="text" id="last_name" className="form-control"
              value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required />
          </div>

          {/* Email */}
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input type="text" id="email" className="form-control"
              value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
          </div>

          {/* Phone Number */}
          <div className="form-group">
            <label htmlFor="phone_number">Phone Number:</label>
            <input type="tel" id="phone_number" className="form-control"
              value={formData.phone_number} onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })} required />
          </div>

          {/* Date of Birth */}
          <div className="form-group">
            <label htmlFor="dob">Date of Birth:</label>
            <input type="date" id="dob" className="form-control"
              value={formData.dob} onChange={(e) => setFormData({ ...formData, dob: e.target.value })} required />
          </div>

          {/* Zip Code */}
          <div className="form-group">
            <label htmlFor="zip_code">Zip Code:</label>
            <input type="text" id="zip_code" className="form-control"
              value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} required />
          </div>

          {/* Password */}
          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <input type={showPassword ? 'text' : 'password'} id="password" className="form-control"
              value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required />
            {passwordError && <p className="error-message">{passwordError}</p>}
            <span
              className={`password-toggle ${showPassword ? 'visible' : ''}`}
              onClick={togglePasswordVisibility}
            >
              {showPassword ? '🙈' : '👁️'}  {/* Eye icon toggle */}
            </span>
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password:</label>
            <input type={showPassword ? 'text' : 'password'} id="confirmPassword" className="form-control"
              value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} required />
            {confirmPasswordError && <p className="error-message">{confirmPasswordError}</p>}
          </div>

          <button type="submit" className="btn-primary">Sign Up</button>
        </form>
        {message && <p className="success-message">{message}</p>}
      </div>
    </div>
  );
};

export default SignUpClient;
